import React, { useState, useEffect } from 'react';
import { Save, XCircle, Loader } from "lucide-react";

// Import shared types
import { Doctor, DoctorFormProps, DoctorFormData } from '../../components/common/types';
import { getAllHeadquarters, getAllLocations, getAllZones } from '../../api/master';
import { useAuth } from '../../context/AuthContext';
import { getAllEmployees } from '../../api/employee';
import EmployeeMain from '../../types/employee';
import { getAllDoctorCategories, getAllDoctorSpecialization } from '../../api/doctor';
import { DoctorCategory, DoctorSpecialization } from '../master/utils/interface';


interface Zone {
  id: number;
  name: string;
}

interface HeadQuarter {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
  head_quarter_id: number;
  latitude: string;
  longitude: string;
}


const DoctorForm: React.FC<DoctorFormProps> = ({ doctor, onSubmit, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<DoctorFormData>({
    tenant_id: user.tenant_id,
    zone_id: 0,
    head_quarter_id: 0,
    location_id: 0,
    specialization_id: 0,
    doctor_category_id: 0,
    employee_id: 0,
    code: "",
    name: "",
    city: "",
    dob: "",
    address: "",
    latitude: "",
    longitude: "",
    hospital_name: "",
    qualification: ""
  });
  const userChangedHq = React.useRef(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [headQuarters, setHeadQuarters] = useState<HeadQuarter[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<EmployeeMain[]>([]);
  const [specializations, setSpecializations] = useState<DoctorSpecialization[]>([]);
  const [docCategories, setDocCategories] = useState<DoctorCategory[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);

  const [coordinatesManuallyChanged, setCoordinatesManuallyChanged] = useState({
    latitude: false,
    longitude: false
  });


  useEffect(() => {
    if (!formData.head_quarter_id) {
      setFilteredLocations([]);
      return;
    }

    const filtered = locations.filter(
      loc => loc.head_quarter_id === formData.head_quarter_id
    );

    setFilteredLocations(filtered);

    // Only reset dependent fields when the user explicitly changed the HQ
    if (!userChangedHq.current) return;
    userChangedHq.current = false;

    setFormData(prev => ({
      ...prev,
      city: "",
      location_id: 0,
      latitude: "",
      longitude: ""
    }));

    setCoordinatesManuallyChanged({
      latitude: false,
      longitude: false
    });

  }, [formData.head_quarter_id, locations]);


  // Fetch zone data
  const fetchZones = async () => {
    try {
      const response = await getAllZones(user.tenant_id);
      setZones(response);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  // Fetch headquarters data
  const fetchHeadQuarters = async () => {
    try {
      const response = await getAllHeadquarters(user.tenant_id);
      setHeadQuarters(response);
    } catch (error) {
      console.error('Error fetching headquarters:', error);
    }
  };

  // Fetch locations data
  const fetchLocations = async () => {
    try {
      const response = await getAllLocations(user.tenant_id);
      setLocations(response);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Fetch employees data
  const fetchEmployees = async () => {
    try {
      const response = await getAllEmployees(user.tenant_id);
      setEmployees(response);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDoctorCategories = async () => {
    try {
      const response = await getAllDoctorCategories(user.tenant_id);
      setDocCategories(response);
    } catch (error) {
      console.error("error fetching doc categories: ", error);
    }
  }

  const fetchAllSpecializations = async () => {
    try {
      const response = await getAllDoctorSpecialization(user.tenant_id);
      setSpecializations(response);
    } catch (error) {
      console.error("error fetching specializations: ", error);
    }
  }

  // Load all API data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchZones(),
        fetchHeadQuarters(),
        fetchLocations(),
        fetchEmployees(),
        fetchDoctorCategories(),
        fetchAllSpecializations()
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  // Initialize form with doctor data if editing
  useEffect(() => {
    if (doctor) {
      setFormData({
        tenant_id: user.tenant_id,
        zone_id: doctor.zone_id,
        head_quarter_id: doctor.head_quarter_id,
        location_id: doctor.location_id,
        specialization_id: doctor.specialization_id || 0,
        doctor_category_id: doctor.doctor_category_id || 0,
        employee_id: doctor.employee_id,
        code: doctor.code,
        name: doctor.name,
        city: doctor.city || "",
        dob: doctor.dob || "",
        address: doctor.address || "",
        latitude: doctor.latitude || "",
        longitude: doctor.longitude || "",
        hospital_name: doctor.hospital_name || "",
        qualification: doctor.qualification || "",
      });

      // If doctor has existing coordinates, mark them as manually set
      if (doctor.latitude || doctor.longitude) {
        setCoordinatesManuallyChanged({
          latitude: !!doctor.latitude,
          longitude: !!doctor.longitude
        });
      }
    } else {
      // For new doctors, set tenant_id from user
      setFormData(prev => ({
        ...prev,
        tenant_id: user.tenant_id
      }));
    }
  }, [doctor, user.tenant_id]);
  useEffect(() => {
    if (!formData.location_id || filteredLocations.length === 0) return;

    const selectedLocation = filteredLocations.find(
      loc => loc.id === formData.location_id
    );

    if (!selectedLocation) return;

    setFormData(prev => ({
      ...prev,
      city: selectedLocation.name,
      latitude: !coordinatesManuallyChanged.latitude
        ? selectedLocation.latitude
        : prev.latitude,
      longitude: !coordinatesManuallyChanged.longitude
        ? selectedLocation.longitude
        : prev.longitude
    }));
  }, [formData.location_id, filteredLocations]);

  // Function to handle city selection
  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCity = e.target.value;

    // Find the location data for the selected city
    const selectedLocation = locations.find(loc => loc.name === selectedCity);

    // Clear error for this field
    if (errors.city) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.city;
        return newErrors;
      });
    }

    // Update form data with city name
    setFormData(prev => ({
      ...prev,
      city: selectedCity,
      // Only auto-fill coordinates if they haven't been manually changed
      latitude: !coordinatesManuallyChanged.latitude && selectedLocation ? selectedLocation.latitude : prev.latitude,
      longitude: !coordinatesManuallyChanged.longitude && selectedLocation ? selectedLocation.longitude : prev.longitude
    }));

    // If city was selected from dropdown (not manually typed), also update location_id
    if (selectedLocation) {
      setFormData(prev => ({
        ...prev,
        location_id: selectedLocation.id
      }));
    }
  };

  // Function to handle manual city input
  const handleManualCityInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cityValue = e.target.value;

    // Clear error for this field
    if (errors.city) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.city;
        return newErrors;
      });
    }

    // Try to find if this matches any location
    const matchingLocation = locations.find(loc =>
      loc.name.toLowerCase().includes(cityValue.toLowerCase())
    );

    setFormData(prev => ({
      ...prev,
      city: cityValue,
      // If matching location found and coordinates not manually changed, update them
      latitude: !coordinatesManuallyChanged.latitude && matchingLocation ? matchingLocation.latitude : prev.latitude,
      longitude: !coordinatesManuallyChanged.longitude && matchingLocation ? matchingLocation.longitude : prev.longitude,
      // If matching location found, set location_id
      location_id: matchingLocation ? matchingLocation.id : prev.location_id
    }));
  };

  // Function to handle coordinate changes (mark them as manually changed)
  const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Mark this coordinate as manually changed
    if (name === 'latitude' && value.trim() !== '') {
      setCoordinatesManuallyChanged(prev => ({ ...prev, latitude: true }));
    }
    if (name === 'longitude' && value.trim() !== '') {
      setCoordinatesManuallyChanged(prev => ({ ...prev, longitude: true }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === 'head_quarter_id') userChangedHq.current = true;

    // Handle number fields
    if (['tenant_id', 'zone_id', 'head_quarter_id', 'location_id', 'specialization_id', 'doctor_category_id', 'employee_id'].includes(name)) {
      setFormData(prev => ({
        ...prev,
        [name]: value ? Number(value) : 0
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!formData.tenant_id) newErrors.tenant_id = "Tenant ID is required";
    if (!formData.zone_id) newErrors.zone_id = "Zone is required";
    if (!formData.head_quarter_id) newErrors.head_quarter_id = "Head Quarter is required";
    if (!formData.city?.trim()) newErrors.city = "City is required";
    if (!formData.employee_id) newErrors.employee_id = "Employee is required";
    if (!formData.name?.trim()) newErrors.name = "Doctor name is required";

    // Date validation for dob
    if (formData.dob) {
      const dobDate = new Date(formData.dob);
      if (isNaN(dobDate.getTime())) {
        newErrors.dob = "Invalid date format";
      } else if (dobDate > new Date()) {
        newErrors.dob = "Date of birth cannot be in the future";
      }
    }

    // Coordinate validation
    if (formData.latitude && (Number(formData.latitude) < -90 || Number(formData.latitude) > 90)) {
      newErrors.latitude = "Latitude must be between -90 and 90";
    }
    if (formData.longitude && (Number(formData.longitude) < -180 || Number(formData.longitude) > 180)) {
      newErrors.longitude = "Longitude must be between -180 and 180";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading form data...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Doctor Code
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="code" className="block text-sm font-medium text-gray-700">
            Doctor Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.code ? 'border-red-500' : 'border-gray-300'
              }`}
            required
            placeholder="e.g., DR001"
          />
          {errors.code && (
            <p className="text-sm text-red-600">{errors.code}</p>
          )}
        </div>
      </div> */}

      {/* Zone and Head Quarter Selection */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="zone_id" className="block text-sm font-medium text-gray-700">
            Zone <span className="text-red-500">*</span>
          </label>
          <select
            id="zone_id"
            name="zone_id"
            value={formData.zone_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${errors.zone_id ? 'border-red-500' : 'border-gray-300'
              }`}
            required
          >
            <option value="0">Select Zone</option>
            {zones.map(zone => (
              <option key={zone.id} value={zone.id}>{zone.name}</option>
            ))}
          </select>
          {errors.zone_id && (
            <p className="text-sm text-red-600">{errors.zone_id}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="head_quarter_id" className="block text-sm font-medium text-gray-700">
            Head Quarter <span className="text-red-500">*</span>
          </label>
          <select
            id="head_quarter_id"
            name="head_quarter_id"
            value={formData.head_quarter_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${errors.head_quarter_id ? 'border-red-500' : 'border-gray-300'
              }`}
            required
          >
            <option value="0">Select Head Quarter</option>
            {headQuarters.map(hq => (
              <option key={hq.id} value={hq.id}>{hq.name}</option>
            ))}
          </select>
          {errors.head_quarter_id && (
            <p className="text-sm text-red-600">{errors.head_quarter_id}</p>
          )}
        </div>
      </div>

      {/* City Selection with Autocomplete */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            City <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              id="city-dropdown"
              name="city"
              value={formData.city}
              onChange={handleCityChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${errors.city ? 'border-red-500' : 'border-gray-300'
                }`}
              required
            >
              <option value="">Select City</option>
              {filteredLocations.map(location => (
                <option key={location.id} value={location.name}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          {errors.city && (
            <p className="text-sm text-red-600">{errors.city}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Select a city
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="employee_id" className="block text-sm font-medium text-gray-700">
            Employee <span className="text-red-500">*</span>
          </label>
          <select
            id="employee_id"
            name="employee_id"
            value={formData.employee_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${errors.employee_id ? 'border-red-500' : 'border-gray-300'
              }`}
            required
          >
            <option value="0">Select Employee</option>
            {employees.map(employee => (
              <option key={employee.id} value={employee.id}>
                {employee.user.name}
              </option>
            ))}
          </select>
          {errors.employee_id && (
            <p className="text-sm text-red-600">{errors.employee_id}</p>
          )}
        </div>
      </div>

      {/* Doctor Name and Date of Birth */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Doctor Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            required
            placeholder="Full name"
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="dob" className="block text-sm font-medium text-gray-700">
            Date of Birth
          </label>
          <input
            type="date"
            id="dob"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.dob ? 'border-red-500' : 'border-gray-300'
              }`}
            max={new Date().toISOString().split('T')[0]}
          />
          {errors.dob && (
            <p className="text-sm text-red-600">{errors.dob}</p>
          )}
        </div>
      </div>

      {/* Static Specialty and Category Selection */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="specialization_id" className="block text-sm font-medium text-gray-700">
            Specialty
          </label>
          <select
            id="specialization_id"
            name="specialization_id"
            value={formData.specialization_id}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="0">Select Specialty</option>
            {specializations.map(specialty => (
              <option key={specialty.id} value={specialty.id}>{specialty.name}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label htmlFor="doctor_category_id" className="block text-sm font-medium text-gray-700">
            Doctor Category
          </label>
          <select
            id="doctor_category_id"
            name="doctor_category_id"
            value={formData.doctor_category_id}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="0">Select Category</option>
            {docCategories.map(category => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </div>
      </div>
      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
        Hospital Name <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        id="hospital_name"
        name="hospital_name"
        value={formData.hospital_name}
        onChange={handleChange}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
        required
        placeholder="Hospital name"
      />
      {errors.hospital_name && (
        <p className="text-sm text-red-600">{errors.hospital_name}</p>
      )}
      {/* Address */}
      <div className="space-y-2">
        <label htmlFor="address" className="block text-sm font-medium text-gray-700">
          Address
        </label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          placeholder="Full address"
        />
      </div>

      {/* Auto-filled Coordinates with Info */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">
            Latitude {coordinatesManuallyChanged.latitude && <span className="text-green-600 text-xs">(Manually set)</span>}
          </label>
          <div className="relative">
            <input
              type="text"
              id="latitude"
              name="latitude"
              value={formData.latitude}
              onChange={handleCoordinateChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.latitude ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., 40.7128"
            />
            {formData.latitude && !coordinatesManuallyChanged.latitude && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Auto-filled
                </span>
              </div>
            )}
          </div>
          {errors.latitude && (
            <p className="text-sm text-red-600">{errors.latitude}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">
            Longitude {coordinatesManuallyChanged.longitude && <span className="text-green-600 text-xs">(Manually set)</span>}
          </label>
          <div className="relative">
            <input
              type="text"
              id="longitude"
              name="longitude"
              value={formData.longitude}
              onChange={handleCoordinateChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.longitude ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., -74.0060"
            />
            {formData.longitude && !coordinatesManuallyChanged.longitude && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Auto-filled
                </span>
              </div>
            )}
          </div>
          {errors.longitude && (
            <p className="text-sm text-red-600">{errors.longitude}</p>
          )}
        </div>
      </div>

      {/* Qualification */}
      <div className="space-y-2">
        <label htmlFor="qualification" className="block text-sm font-medium text-gray-700">
          Qualification
        </label>
        <textarea
          id="qualification"
          name="qualification"
          value={formData.qualification}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          placeholder="Medical qualifications, degrees, etc."
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <XCircle className="w-4 h-4 mr-2" />
          Cancel
        </button>
        <button
          type="submit"
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Save className="w-4 h-4 mr-2" />
          {doctor ? 'Update Doctor' : 'Save Doctor'}
        </button>
      </div>
    </form>
  );
};

export default DoctorForm;